import { showErrorToast, showSuccessToast } from '@slr/shared-library';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { usePlatformAdminUsersApi } from 'services/admin/usePlatformAdminUsersApi';
import { PLATFORM_ADMIN_USERS_QUERY_KEY } from './useSearchPlatformAdminUsers';

type PlatformAdminMutationError = Error & {
  uniqueExceptionType?: string;
};

const LAST_PLATFORM_ADMIN_REMAINING = 'LAST_PLATFORM_ADMIN_REMAINING';
const CANNOT_DISABLE_OWN_ACCOUNT = 'CANNOT_DISABLE_OWN_ACCOUNT';
const CANNOT_REVOKE_OWN_PLATFORM_ADMINISTRATOR_ROLE =
  'CANNOT_REVOKE_OWN_PLATFORM_ADMINISTRATOR_ROLE';

const showPlatformAdminMutationErrorToast = (
  error: PlatformAdminMutationError
) => {
  if (error.uniqueExceptionType === LAST_PLATFORM_ADMIN_REMAINING) {
    showErrorToast('toasts.platformAdminUsers.lastAdminRemaining');
    return;
  }
  if (error.uniqueExceptionType === CANNOT_DISABLE_OWN_ACCOUNT) {
    showErrorToast('toasts.platformAdminUsers.cannotDisableSelf');
    return;
  }
  if (error.uniqueExceptionType === CANNOT_REVOKE_OWN_PLATFORM_ADMINISTRATOR_ROLE) {
    showErrorToast('toasts.platformAdminUsers.cannotRevokeOwnAdminRole');
    return;
  }
  showErrorToast('toasts.platformAdminUsers.actionFailed');
};

export const useSetPlatformAdminUserEnabled = () => {
  const { setUserEnabled } = usePlatformAdminUsersApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ userId, enabled }: { userId: string; enabled: boolean }) =>
      setUserEnabled(userId, enabled),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [PLATFORM_ADMIN_USERS_QUERY_KEY]
      });
      showSuccessToast('toasts.platformAdminUsers.enabledUpdated');
    },
    onError: (error: PlatformAdminMutationError) => {
      showPlatformAdminMutationErrorToast(error);
    }
  });
};

export const useSetPlatformAdministratorRole = () => {
  const { setPlatformAdministrator } = usePlatformAdminUsersApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ userId, granted }: { userId: string; granted: boolean }) =>
      setPlatformAdministrator(userId, granted),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [PLATFORM_ADMIN_USERS_QUERY_KEY]
      });
      showSuccessToast('toasts.platformAdminUsers.roleUpdated');
    },
    onError: (error: PlatformAdminMutationError) => {
      showPlatformAdminMutationErrorToast(error);
    }
  });
};
